/* HTTP client.
 *
 * - Base URL comes from VITE_API_BASE at build time; in dev it defaults to
 *   the docker-compose backend at 127.0.0.1:8000.
 * - JWT lives in localStorage under 'vilka-token'. Every request attaches it
 *   automatically when present.
 * - Errors are normalised to { code, message, status, details? }. UI code
 *   only ever has to look at err.code.
 * - A single 'backend-unreachable' event is dispatched on the window when a
 *   request fails for network reasons (or the readiness probe). The App
 *   subscribes and shows a full-screen "service unavailable" gate.
 */

// Vite replaces /api at build time via `define`. In dev that's the
// docker-compose backend; in the production image that's the literal token
// "/api" (set via VITE_API_BASE in frontend/Dockerfile), which the
// container entrypoint sed-replaces with the real URL at boot. This indirection
// lets one image serve any environment.
//
// The trailing whitespace keeps the placeholder grep-able after JS minification
// — see frontend/entrypoint.sh.
const RAW_BASE = /api;
const API_BASE = RAW_BASE.replace(/\/$/, '') || 'http://127.0.0.1:8000';

const TOKEN_KEY = 'vilka-token';

export function getToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* private mode */ }
}
export function clearToken() { setToken(null); }


export class ApiError extends Error {
  constructor({ code, message, status, details }) {
    super(message || code || `HTTP ${status}`);
    this.code = code;
    this.status = status;
    this.details = details;
  }
}


// Пара локализованных лейблов для самых частых полей, на которые валится
// pydantic-валидация. Если поля нет в этой карте — берём последнюю часть
// loc как fallback.
const FIELD_LABELS_RU = {
  skills: 'Навыки',
  role: 'Роль',
  region: 'Регион',
  experience: 'Опыт',
  education: 'Образование',
  languages: 'Языки',
  employment: 'Тип занятости',
};

function humanizeFastapiError(errors) {
  const first = errors[0];
  const loc = Array.isArray(first.loc) ? first.loc.filter((p) => p !== 'body') : [];
  const field = loc[0];
  const label = FIELD_LABELS_RU[field] || field || 'поле';
  const ctx = first.ctx || {};

  switch (first.type) {
    case 'too_long': {
      const max = ctx.max_length ?? ctx.actual_length;
      if (field === 'skills' && max != null) {
        return `Сейчас в резюме слишком много навыков. Оставьте не больше ${max} — без этого расчёт не запустится.`;
      }
      return `Поле «${label}»: значений больше, чем допустимо${max != null ? ` (максимум ${max})` : ''}.`;
    }
    case 'too_short': {
      const min = ctx.min_length;
      return `Поле «${label}» нужно заполнить${min != null && min > 1 ? ` (минимум ${min} символов)` : ''}.`;
    }
    case 'missing':
      return `Поле «${label}» обязательно.`;
    case 'enum':
    case 'literal_error':
      return `Поле «${label}»: выбранного значения нет в справочнике.`;
    case 'string_too_long':
      return `Поле «${label}»: текст слишком длинный${ctx.max_length != null ? ` (максимум ${ctx.max_length} символов)` : ''}.`;
    default:
      return `Поле «${label}»: ${first.msg || 'значение не прошло проверку'}.`;
  }
}


function emitUnreachable() {
  // Lets the App render a gate without prop-drilling through every call site.
  window.dispatchEvent(new CustomEvent('vilka:backend-unreachable'));
}


async function request(method, path, { body, query, headers, auth = true } = {}) {
  // API_BASE is "http://127.0.0.1:8000" in dev (absolute) or "/api" in prod
  // (relative, when the SPA is served behind the same edge as the backend).
  // `new URL(spec)` with one arg REQUIRES an absolute URL — passing "/api/..."
  // throws TypeError synchronously, which used to surface as the BackendGate
  // because the boot promise rejected before any fetch ever fired. Resolve
  // against window.location.origin so relative bases work uniformly.
  const url = new URL(API_BASE + path, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null && v !== '') url.searchParams.append(k, v);
    }
  }

  const reqHeaders = { 'Accept': 'application/json', ...(headers || {}) };
  if (body !== undefined) reqHeaders['Content-Type'] = 'application/json';
  if (auth) {
    const tok = getToken();
    if (tok) reqHeaders['Authorization'] = `Bearer ${tok}`;
  }

  let response;
  try {
    response = await fetch(url.toString(), {
      method,
      headers: reqHeaders,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (networkErr) {
    emitUnreachable();
    throw new ApiError({
      code: 'network_error',
      message: 'Сервис недоступен',
      status: 0,
      details: { cause: String(networkErr) },
    });
  }

  // 204 No Content
  if (response.status === 204) return null;

  // Try to parse JSON. If it's not JSON, leave payload empty.
  let payload = null;
  const ctype = response.headers.get('content-type') || '';
  if (ctype.includes('application/json')) {
    try { payload = await response.json(); } catch { /* ignore */ }
  }

  if (!response.ok) {
    // Backend's stable envelope: { detail: { error: { code, message, details? } } }
    const err = payload && payload.detail && payload.detail.error;
    if (err) {
      // Token might have expired, or the user it pointed at no longer
      // exists (e.g. after a dev `docker compose down -v` wipes the DB).
      // Either way the cached JWT is unusable — drop it so the next boot
      // refetches a fresh anon session via /auth/anon.
      if (response.status === 401) clearToken();
      throw new ApiError({
        code: err.code,
        message: err.message,
        status: response.status,
        details: err.details,
      });
    }
    // FastAPI validation errors (422) живут под `detail` массивом
    // {loc, type, msg, ctx?}. Подбираем самое полезное человеческое
    // сообщение из первого item'а — этого хватает для UI-плашки.
    const fastapiErrors = response.status === 422 && Array.isArray(payload && payload.detail)
      ? payload.detail
      : null;
    if (fastapiErrors && fastapiErrors.length > 0) {
      throw new ApiError({
        code: 'validation_error',
        message: humanizeFastapiError(fastapiErrors),
        status: response.status,
        details: fastapiErrors,
      });
    }
    throw new ApiError({
      code: 'http_error',
      message: `HTTP ${response.status}`,
      status: response.status,
      details: payload && payload.detail,
    });
  }

  return payload;
}


export const api = {
  get:    (path, opts)        => request('GET',    path, opts),
  post:   (path, body, opts)  => request('POST',   path, { ...opts, body }),
  put:    (path, body, opts)  => request('PUT',    path, { ...opts, body }),
  patch:  (path, body, opts)  => request('PATCH',  path, { ...opts, body }),
  delete: (path, opts)        => request('DELETE', path, opts),
};


/* Readiness probe with retry-friendly callback.
 * Used on app boot to gate the UI behind a live backend. */
export async function pingHealth() {
  return request('GET', '/health', { auth: false });
}
